import type { ErrorType, EvcI18nService, HttpError } from '@evc/platform';
import { ErrorTypes, HttpCode } from '@evc/platform';

/** infer correct error typing using our API conventions
 * - define error `type` based on HTTP status code
 * - help with error message fallback (@see : shared i18n for generic error messages)
 */
export class TypedError extends Error {
  type: ErrorType;

  constructor(error: HttpError, i18nService?: EvcI18nService) {
    const { errors } = error.error ?? {};
    const firstError = errors?.[0];

    const type = ((errorStatus) => {
      switch (errorStatus) {
        case HttpCode.BadRequest:
          return ErrorTypes.INVALID;
        case HttpCode.Unauthorized:
          return ErrorTypes.UNAUTHORIZED;
        case HttpCode.UnprocessableEntity:
          return ErrorTypes.UNPROCESSABLE;
        case HttpCode.GatewayError:
        case HttpCode.InternalServerError:
          return ErrorTypes.INTERNAL;
        case HttpCode.Forbidden:
          return ErrorTypes.FORBIDDEN;
        default:
          return ErrorTypes.UNKNOWN;
      }
    })(error.status);

    const message = (() => {
      if (type === ErrorTypes.INTERNAL) return type;

      if (firstError?.message) return firstError.message;

      return type === ErrorTypes.UNKNOWN ? error.message : type;
    })();

    const translatedMessage = (() => {
      const translated = i18nService?.t(`errors.${message}`);

      return translated && !translated.startsWith('errors.') ? translated : message;
    })();

    super(translatedMessage);

    if (typeof error === 'object') Object.assign(this, error);
    Object.setPrototypeOf(this, new.target.prototype);

    this.type = type;
    this.message = translatedMessage;

    return this;
  }
}
